import { ReactNode, useState } from 'react';
import { OverlayedLoader } from '@mews-commander/ui';
import { IconButton, IconName, Stack } from '@optimus-web/core';
import { getCustomerFullName } from '@mews-commander/legacy-entities/src/ducks/customers';
import { Companionship, useRepositoryMutation } from '@mews-commander/data-access';
import {
    BillingAutomationChangesType,
    useCommanderSelector,
    useCommanderThunkDispatch,
    useGainsightCommanderEvents,
} from '@mews-commander/utils';
import { WithScope } from '@mews-commander/types';
import { lt, showSuccessAlert, useFeatureFlag } from '@mews/framework';
import { deleteCompanionships as deleteCompanionshipsFromRedux } from '@mews-commander/legacy-entities/src/ducks/companionships';
import { FeatureFlag } from '@mews-commander/enums';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { getReservationsCustomerIds } from '@mews-commander/legacy-entities/src/ducks/reservations';
import { editProfile } from '../../../actions/profile';
import { getFirstCheckedReservationId } from '../../../selectors';
import { useRoutingRuleChangesConfirmation } from '../../../../routingRulesNew/components/RoutingRuleChangeConfirmationPanel/useRoutingRuleChangesConfirmation';

interface UnassignedCompanionsListContainerProps extends WithScope {
    onSuccess?: () => void;
    companionship: Companionship;
}

export const UnassignedCompanionsListItemContainer = ({
    companionship,
    scope,
    onSuccess,
}: UnassignedCompanionsListContainerProps) => {
    const name = useCommanderSelector((state) => getCustomerFullName(state, { id: companionship.companionId }));
    const [routingRuleChangesDialog, setRoutingRuleChangesDialog] = useState<ReactNode>(null);
    const granularRoutingRulesConditionsExtensionEnabled = useFeatureFlag(
        FeatureFlag.granularRoutingRulesConditionsExtensionEnabled
    );
    const enterpriseId = useCurrentEnterpriseId();
    const firstCheckedReservationId = useCommanderSelector((state) => getFirstCheckedReservationId(state, { scope }));
    const reservationIds = firstCheckedReservationId ? [firstCheckedReservationId] : [];
    const currentAccountIds = useCommanderSelector((state) =>
        getReservationsCustomerIds(state, {
            ids: reservationIds,
            enterpriseId,
        })
    );
    const gainsightEvents = useGainsightCommanderEvents();

    const onApplyBillingAutomationChanges = (changes: BillingAutomationChangesType) => {
        gainsightEvents.onApiSuccess({
            context: 'BillingAutomationChanged',
            data: {
                changes,
                origin: 'Account',
            },
        });
    };

    const { validate: validateDeleteCompanion } = useRoutingRuleChangesConfirmation({
        reservationIds,
        accountIds: currentAccountIds.filter((id: string) => id !== companionship.companionId),
        enabled: granularRoutingRulesConditionsExtensionEnabled && !!reservationIds.length,
        scope,
        onSubmit: ({ changes }) => {
            deleteCompanionshipMutation(
                {
                    companionshipId: companionship.id,
                },
                { onSuccess: () => onApplyBillingAutomationChanges(changes) }
            );
            setRoutingRuleChangesDialog(null);
        },
        onCancel: () => setRoutingRuleChangesDialog(null),
    });

    const { validate: validateUpdateCompanion } = useRoutingRuleChangesConfirmation({
        reservationIds,
        accountIds: [...currentAccountIds, companionship.companionId],
        enabled: granularRoutingRulesConditionsExtensionEnabled && !!reservationIds.length,
        scope,
        onSubmit: ({ changes }) => {
            updateCompanionshipMutation(
                {
                    companionshipId: companionship.id,
                    reservationId: firstCheckedReservationId,
                },
                { onSuccess: () => onApplyBillingAutomationChanges(changes) }
            );
            setRoutingRuleChangesDialog(null);
        },
        onCancel: () => setRoutingRuleChangesDialog(null),
    });

    const dispatch = useCommanderThunkDispatch();
    const handleEditProfile = () => {
        dispatch(editProfile({ accountId: companionship.companionId }));
    };

    const { mutate: deleteCompanionshipMutation, isPending: isLoadingDeleteCompanionship } = useRepositoryMutation(
        'companionships',
        'delete',
        {
            invalidateQueries: { routingRules: ['getDetailsOnReservationUpdate'] },
            onSuccess: () => {
                // ToDo: Remove this when the new reservation module is fully migrated and just invalidate queries inside submit form mutations
                dispatch(
                    deleteCompanionshipsFromRedux({
                        companionshipId: companionship.id,
                    })
                );
                onSuccess?.();
                showSuccessAlert({ description: lt('CompanionRemoved') });
            },
        }
    );

    const deleteCompanionship = () => {
        const result = validateDeleteCompanion();

        if (result) {
            setRoutingRuleChangesDialog(result.dialog);
            return;
        }

        deleteCompanionshipMutation({
            companionshipId: companionship.id,
        });
    };

    const { mutate: updateCompanionshipMutation, isPending: isLoadingUpdateCompanionship } = useRepositoryMutation(
        'companionships',
        'update',
        {
            invalidateQueries: { routingRules: ['getDetailsOnReservationUpdate'] },
            onSuccess: () => {
                onSuccess?.();
                showSuccessAlert({ description: lt('AddCompanionSuccessMessage') });
            },
        }
    );

    const updateCompanionship = () => {
        const result = validateUpdateCompanion();

        if (result) {
            setRoutingRuleChangesDialog(result.dialog);
            return;
        }

        updateCompanionshipMutation({
            companionshipId: companionship.id,
            reservationId: firstCheckedReservationId,
        });
    };

    return (
        <>
            <UnassignedCompanionsListItem
                name={name}
                isLoading={isLoadingDeleteCompanionship || isLoadingUpdateCompanionship}
                handleEditProfile={handleEditProfile}
                deleteCompanionship={deleteCompanionship}
                updateCompanionship={updateCompanionship}
            />
            {routingRuleChangesDialog}
        </>
    );
};

interface UnassignedCompanionsListProps {
    name: string;
    isLoading: boolean;
    handleEditProfile: () => void;
    deleteCompanionship: () => void;
    updateCompanionship: () => void;
}

export const UnassignedCompanionsListItem = ({
    deleteCompanionship,
    handleEditProfile,
    isLoading,
    name,
    updateCompanionship,
}: UnassignedCompanionsListProps) => {
    return (
        <Stack itemAlignment="center" distribution="space-between">
            <OverlayedLoader show={isLoading} />
            <Stack itemAlignment="center" spacing="100">
                {name}
                <IconButton
                    type="button"
                    variant="ghost"
                    icon={IconName.Profile}
                    size="small"
                    onClick={handleEditProfile}
                />
            </Stack>
            <Stack itemAlignment="center">
                <IconButton
                    type="button"
                    variant="ghost"
                    icon={IconName.CrossClose}
                    size="small"
                    onClick={deleteCompanionship}
                />
                <IconButton
                    type="button"
                    variant="ghost"
                    icon={IconName.ChevronRight}
                    size="small"
                    onClick={updateCompanionship}
                />
            </Stack>
        </Stack>
    );
};
